import { JSX } from "solid-js";

import Trans from "@repo/localization/jsx.macro";
import t from "@repo/localization/string.macro";
import Card from "@repo/ui/containers/Card";
import ScrollView from "@repo/ui/containers/ScrollView";
import Constraints from "@repo/ui/layout/Constraints";

import bg from "./images/kikoff-arrow.svg";
import qrCode from "./images/qr-onelink.svg";

import styles from "./AuthLayout.module.scss";

declare namespace Square {
  type Props = {
    icon: JSX.Element;
    title: string;
    text: string;
  };
}

function Square({ icon, title, text }: D<Square.Props>) {
  return (
    <Card class={styles.square}>
      <div class="text:heading-2">{icon}</div>
      <div class="text:heading-3++">{title}</div>
      <div class="text:mini text-center">{text}</div>
    </Card>
  );
}

declare namespace AuthLayout {
  type Props = {
    class?: string;
    sideContent?: JSX.Element;
    children?: JSX.Element;
  };
}

function AuthLayout({
  class: className,
  sideContent,
  children,
}: D<AuthLayout.Props>) {
  return (
    <div class={`${styles["desktop-background"]} ${className}`}>
      <aside
        style={{
          "background-image": `url("${bg}")`,
          "background-size": "cover",
          "background-position": "center",
        }}
      >
        <Constraints maxWidth="600px">{sideContent}</Constraints>
      </aside>
      <main class="h-full">
        <ScrollView>
          <Constraints
            class={`${styles.content} py-3 h-full center-child`}
            maxWidth="480px"
          >
            <div>{children}</div>
          </Constraints>
        </ScrollView>
      </main>
    </div>
  );
}

declare namespace AuthLayout.MobileOnly {
  type Props = {
    children: JSX.Element;
  };
}

AuthLayout.MobileOnly = ({ children }: D<AuthLayout.MobileOnly.Props>) => (
  <div class={styles["mobile-only"]}>{children}</div>
);

declare namespace AuthLayout.Login {
  type Props = Omit<AuthLayout.Props, "sideContent">;
}

AuthLayout.Login = ({ children, ...props }: D<AuthLayout.Login.Props>) => (
  <AuthLayout
    {...props}
    sideContent={
      !import.meta.env.MOBILE && (
        <div class="center-child">
          <Card class={styles["login-square"]}>
            <div class="text:heading-4++">
              <Trans>Download the Kikoff App for the best experience</Trans>
            </div>
            <img src={qrCode} alt={t`QR code`} />
            <div class="text:small">
              <Trans>Scan the QR code to get started</Trans>
            </div>
          </Card>
        </div>
      )
    }
  >
    {children}
  </AuthLayout>
);

declare namespace AuthLayout.Signup {
  type Props = Omit<AuthLayout.Props, "sideContent">;
}

AuthLayout.Signup = ({ children, ...props }: D<AuthLayout.Signup.Props>) => (
  <AuthLayout
    {...props}
    sideContent={
      <div class={styles["signup-squares"]}>
        <div>
          <Square
            icon=""
            title={t`Fast`}
            text={t`No credit check, no credit pull. Report on payments to all 3 bureaus.`}
          />
          <Square
            icon=""
            title={t`Affordable`}
            text={t`Starting at $5/month, enjoy tools designed to help you build and grow your credit.`}
          />
        </div>
        <div>
          <Square
            icon=""
            title={t`+84 pts*`}
            text={t`Users with starting credit under 600 grew an average of +84pts in just a year with on-time payments.`}
          />
          <Square
            icon=""
            title={t`Secure`}
            text={t`Bank-level security keeps your data safe.`}
          />
        </div>
      </div>
    }
  >
    {children}
  </AuthLayout>
);

export default AuthLayout;
